import { withTranslation } from 'react-i18next';

import Loading from '../../../../components/Loading';
import MetricCards from '../../GscTools/components/MetricCards';
import PeriodTrend from '../../GscTools/components/PeriodTrend';
import { comparability, metricsFor } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { segmentKeyLabel, segmentLabel } from '../segments';

/**
 * Block 02 — one segment's own daily history, in both windows.
 *
 * The selected segment is a scope chip rather than a mode the page drops into: it is visible,
 * it says which dimension it belongs to, and removing it returns the report to all segments
 * without touching anything else the user chose.
 *
 * The figures are the sums of the days actually returned, through the shared totals, so the cards
 * and the lines agree with each other and a day Search Console had nothing for is a gap in both
 * rather than a zero in either. CTR is clicks over impressions across those days, never the mean
 * of the days' own CTRs.
 */
function SegmentTrend({
    t,
    scope,
    status,
    error,
    selectedKey,
    current,
    baseline,
    metric,
    onMetric,
    onClear,
}) {
    const comparison = comparability(current?.coverage, baseline?.coverage ?? null);
    const label = selectedKey ? segmentKeyLabel(t, scope.segment, selectedKey) : '';

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.audience_appearance.trend.title')}</p>
                    <p className="gsc-tool-sub">
                        {selectedKey
                            ? `${formatPeriod(scope.current)}`
                            : t('tools.audience_appearance.trend.description')}
                    </p>

                    {!!selectedKey && (
                        <div className="gsc-tool-chips">
                            <span className="gsc-tool-chip">
                                <span>
                                    {segmentLabel(t, scope.segment)}: {label}
                                    {label !== selectedKey && ` (${selectedKey})`}
                                </span>
                                <button
                                    type="button"
                                    aria-label={t('tools.audience_appearance.trend.clear')}
                                    title={t('tools.audience_appearance.trend.clear')}
                                    onClick={onClear}
                                >
                                    <i className="icon-x-close" />
                                </button>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="card-body">
                {!selectedKey && (
                    <div className="gsc-tool-load-state">
                        <span className="gsc-tool-load-icon" aria-hidden="true">
                            <i className="icon-area-chart" />
                        </span>
                        <h4>{t('tools.audience_appearance.trend.idle_title')}</h4>
                        <p>{t('tools.audience_appearance.trend.idle')}</p>
                    </div>
                )}

                {!!selectedKey && status === 'loading' && <Loading padding={20} />}

                {!!selectedKey && status === 'error' && (
                    <div className="gsc-tool-banner error">
                        {error ?? t('global.something_went_wrong')}
                    </div>
                )}

                {!!selectedKey && status === 'done' && (
                    <>
                        <MetricCards
                            metrics={metricsFor(scope.searchType)}
                            totals={current?.totals}
                            priorTotals={baseline?.totals}
                            comparison={comparison}
                            selectedMetric={metric}
                            onSelectMetric={onMetric}
                            noteFor={(name, defaultNote) =>
                                current?.coverage
                                    ? t('tools.audience_appearance.trend.sum_note', {
                                          days: current.coverage.observed.length,
                                      })
                                    : defaultNote
                            }
                        />

                        <p className="gsc-tool-hint">
                            {t('tools.audience_appearance.trend.pick_card')}
                        </p>

                        <PeriodTrend
                            metric={metric}
                            current={current}
                            baseline={baseline}
                            fileName={t('tools.audience_appearance.trend.title')}
                        />

                        <p className="gsc-tool-table-note">
                            {scope.takesFilters
                                ? t('tools.audience_appearance.trend.filters_note')
                                : t('tools.audience_appearance.trend.unfiltered_note')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(SegmentTrend);
